import { supabase } from "@/integrations/supabase/client";
import { track } from "@/lib/tracking";

const clientToken = import.meta.env.VITE_PAYMENTS_CLIENT_TOKEN;

declare global {
  interface Window {
    Paddle: any;
  }
}

let paddleInitialized = false;

// Paddle.js v2 event names we care about for tracking.
// docs: checkout.loaded, checkout.completed, checkout.closed, checkout.payment.selected
function handlePaddleEvent(evt: any) {
  try {
    const name: string = evt?.name ?? "";
    const data = evt?.data ?? {};
    const items = Array.isArray(data?.items) ? data.items : [];
    const firstItem = items[0] ?? {};
    const common = {
      provider: "paddle" as const,
      environment: clientToken?.startsWith("test_") ? "sandbox" : "live",
      tier: data?.custom_data?.tier ?? null,
      transaction_id: data?.transaction_id ?? null,
      amount_total: data?.totals?.total ?? null,
      currency: data?.currency_code ?? null,
      price_id: firstItem?.price_id ?? null,
      quantity: firstItem?.quantity ?? null,
    };
    if (name === "checkout.completed") {
      track("purchase_completed", { entity_type: "order", entity_id: common.transaction_id, ...common });
    } else if (name === "checkout.closed") {
      track("purchase_abandoned", { entity_type: "order", entity_id: common.transaction_id, ...common });
    } else if (name === "checkout.payment.selected") {
      track("purchase_payment_selected", { ...common, payment_method: data?.payment?.method_details?.type ?? null });
    }
  } catch { /* never break checkout */ }
}

export async function initializePaddle() {
  if (paddleInitialized) return;

  if (!clientToken) {
    throw new Error("Payments client token is not set");
  }

  return new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://cdn.paddle.com/paddle/v2/paddle.js";
    script.onload = () => {
      const environment = clientToken.startsWith("test_") ? "sandbox" : "production";
      window.Paddle.Environment.set(environment);
      window.Paddle.Initialize({ token: clientToken, eventCallback: handlePaddleEvent });
      paddleInitialized = true;
      resolve();
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

export async function getPaddlePriceId(priceId: string): Promise<string> {
  const environment = clientToken?.startsWith("test_") ? "sandbox" : "live";
  const { data, error } = await supabase.functions.invoke("get-paddle-price", {
    body: { priceId, environment },
  });
  if (error || !data?.paddleId) {
    throw new Error(`Failed to resolve price: ${priceId}`);
  }
  return data.paddleId;
}

export function getPaymentEnvironment(): "sandbox" | "live" {
  return clientToken?.startsWith("test_") ? "sandbox" : "live";
}
